import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';

const Bar = ({ pct, ok }) => (
  <span className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
    <span className={`block h-full ${ok ? 'bg-green-500' : 'bg-orange-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
  </span>
);

export const DegreeAudit = ({ studentId, compact = false }) => {
  const { data, isError } = useQuery({ enabled: !!studentId, retry: false, queryKey: ['degree-audit', studentId],
    queryFn: () => api.get(`/acad/degree-audit/${studentId}`).then((r) => r.data) });
  const [progIx, setProgIx] = useState(0);
  const [gapsOnly, setGapsOnly] = useState(false);
  if (isError || !data) return null;
  if (!data.audits.length) return <p className="text-sm text-slate-400" data-testid="audit-empty">No programme on the academic record yet - the degree audit appears once a programme is added.</p>;
  const audit = data.audits[Math.min(progIx, data.audits.length - 1)];
  const cats = audit.categories.filter((c) => !gapsOnly || !c.satisfied);
  return (
    <div data-testid={`degree-audit-${studentId}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {data.audits.length > 1 && data.audits.map((a, i) => (
          <button key={a.record_id} data-testid={`audit-prog-${a.programme_code}`} onClick={() => setProgIx(i)}
            className={`text-xs px-2 py-1 rounded-full ${i === progIx ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>{a.programme_code}</button>
        ))}
        <label className="text-xs text-slate-600 flex items-center gap-1 ms-auto cursor-pointer">
          <input data-testid="audit-gaps-only" type="checkbox" checked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)} /> Show gaps only
        </label>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div className="text-sm">
          <b>{audit.programme_code}</b> {audit.programme_name}
          {audit.complete
            ? <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">Requirements met</span>
            : <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">In progress</span>}
        </div>
        {audit.overall_pct !== null && (
          <span className="flex items-center gap-2 flex-1 max-w-xs text-xs text-slate-500">
            <Bar pct={audit.overall_pct} ok={audit.complete} />
            <b data-testid="audit-overall-pct">{audit.overall_pct}%</b>
          </span>
        )}
        <span className="text-xs text-slate-500">{audit.credits_counted}/{audit.credit_requirement || '-'} credits</span>
      </div>
      {cats.map((c) => (
        <div key={c.category} className="border-b border-slate-50 py-2" data-testid={`audit-cat-${c.category}`}>
          <div className="flex items-center gap-2 text-sm">
            {c.satisfied ? <CheckCircle size={15} className="text-green-600" /> : <WarningCircle size={15} className="text-orange-500" />}
            <b>{c.category}</b>
            <span className="text-xs text-slate-400">{c.earned}/{c.needed} credits{c.elective_credits ? ` (${c.required_credits} required + ${c.elective_credits} elective)` : ''}</span>
            <span className="flex items-center gap-2 flex-1 max-w-[10rem] ms-auto"><Bar pct={c.pct} ok={c.satisfied} /></span>
          </div>
          {!c.satisfied && !compact && (
            <div className="mt-1.5 ms-6">
              <p className="text-xs text-slate-500 mb-1">{c.remaining} credit(s) still missing{c.suggestions.length ? ' - courses that would count:' : ''}</p>
              <div className="flex gap-1.5 flex-wrap">
                {c.suggestions.map((sug) => (
                  <span key={`${c.category}-${sug.code}`} className={`text-[11px] px-2 py-0.5 rounded-full border ${sug.in_progress ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600'}`}>
                    {sug.code} · {sug.credits} cr{sug.in_progress ? ' · in progress' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
      {gapsOnly && cats.length === 0 && <p className="text-sm text-green-700" data-testid="audit-no-gaps">No gaps - every category is satisfied.</p>}
      {data.in_progress.length > 0 && !compact && (
        <p className="text-xs text-slate-500 mt-2">Currently taking: {data.in_progress.map((r) => `${r.course_code} (${r.status})`).join(', ')}</p>
      )}
    </div>
  );
};
